import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ResourceCard from "../components/ResourceCard";
import { publicService } from "../services/publicService";
import {
	getSelectedResourceIds,
	upsertSelectedResource,
	removeSelectedResource,
} from "../utils/inquirySelection";

export default function ResourceDirectory() {
	const PAGE_SIZE = 12;
	const [resources, setResources] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [filtersReady, setFiltersReady] = useState(false);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [filterRole, setFilterRole] = useState("");
	const [filterCategory, setFilterCategory] = useState("");
	const [sortBy, setSortBy] = useState("id");
	const [allRoles, setAllRoles] = useState([]);
	const [allCategories, setAllCategories] = useState([]);
	const [selectedResourceIds, setSelectedResourceIds] = useState([]);
	const [commentModal, setCommentModal] = useState({
		open: false,
		resourceId: null,
		resourceName: "",
		comment: "",
	});

	useEffect(() => {
		setSelectedResourceIds(getSelectedResourceIds());
	}, []);

	useEffect(() => {
		Promise.all([publicService.getRoles(), publicService.getCategories()])
			.then(([rolesData, categoriesData]) => {
				setAllRoles((rolesData || []).map((role) => role.name));
				setAllCategories(
					(categoriesData || []).map((category) => category.name),
				);
			})
			.finally(() => {
				setFiltersReady(true);
			});
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, filterRole, filterCategory, sortBy]);

	useEffect(() => {
		if (!filtersReady) {
			return;
		}

		setIsFetching(true);

		publicService
			.getResources({
				search: debouncedSearch,
				role: filterRole,
				category: filterCategory,
				sortBy,
				page,
				limit: PAGE_SIZE,
			})
			.then((data) => {
				setResources(data?.data || []);
				setTotal(data?.total || 0);
				setTotalPages(data?.pages || 1);
			})
			.catch(() => {
				setResources([]);
				setTotal(0);
				setTotalPages(1);
			})
			.finally(() => {
				setLoading(false);
				setIsFetching(false);
			});
	}, [debouncedSearch, filterRole, filterCategory, sortBy, filtersReady, page]);

	const clearFilters = () => {
		setSearch("");
		setFilterRole("");
		setFilterCategory("");
		setSortBy("id");
		setPage(1);
	};

	const handleToggleSelect = (resourceId) => {
		const next = removeSelectedResource(resourceId).map((item) => item.id);
		setSelectedResourceIds(next);
	};

	const openCommentModal = (resourceId, resource) => {
		const resourceName =
			`${resource?.first_name || ""} ${resource?.last_name || ""}`.trim() ||
			resource?.role ||
			`Resource ID-${resourceId}`;

		setCommentModal({
			open: true,
			resourceId,
			resourceName,
			comment: "",
		});
	};

	const closeCommentModal = () => {
		setCommentModal({
			open: false,
			resourceId: null,
			resourceName: "",
			comment: "",
		});
	};

	const confirmSelectionWithComment = () => {
		const comment = commentModal.comment.trim();
		if (!comment) {
			return;
		}

		const next = upsertSelectedResource(commentModal.resourceId, comment).map(
			(item) => item.id,
		);
		setSelectedResourceIds(next);
		closeCommentModal();
	};

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 flex items-center justify-center min-h-64 py-24">
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-gray-600">Loading resources...</p>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />
			<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				{/* Header */}
				<div className="mb-8">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Resource Directory
					</h1>
					<p className="text-gray-600">
						Browse and filter our pool of {total} technical experts
					</p>
				</div>

				{/* Filters */}
				<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<div className="relative">
							<svg
								className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
								/>
							</svg>
							<input
								type="text"
								placeholder="Search by role, skill, ID..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="input-field pl-10"
							/>
						</div>
						<select
							value={filterRole}
							onChange={(e) => setFilterRole(e.target.value)}
							className="input-field"
						>
							<option value="">All Roles</option>
							{allRoles.map((role) => (
								<option key={role} value={role}>
									{role}
								</option>
							))}
						</select>
						<select
							value={filterCategory}
							onChange={(e) => setFilterCategory(e.target.value)}
							className="input-field"
						>
							<option value="">All Technologies</option>
							{allCategories.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>
						<select
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value)}
							className="input-field"
						>
							<option value="id">Sort by ID</option>
							<option value="exp-desc">Experience (High → Low)</option>
							<option value="exp-asc">Experience (Low → High)</option>
						</select>
					</div>
					{(search || filterRole || filterCategory) && (
						<div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
							<span className="text-sm text-gray-600">
								{total} result{total === 1 ? "" : "s"} found
							</span>
							{isFetching && (
								<span className="text-sm text-gray-500">Updating...</span>
							)}
							<button
								onClick={clearFilters}
								className="text-sm text-primary-600 hover:text-primary-800 font-medium"
							>
								Clear filters
							</button>
						</div>
					)}
				</div>

				{/* Results */}
				{resources.length === 0 ? (
					<div className="text-center py-16 bg-white rounded-xl border border-gray-200">
						<div className="text-4xl mb-4">🔍</div>
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							No resources found
						</h3>
						<p className="text-gray-600 mb-4">
							Try adjusting your search or filters
						</p>
						<button onClick={clearFilters} className="btn-primary">
							Clear Filters
						</button>
					</div>
				) : (
					<>
						<div className="text-sm text-gray-500 mb-4">
							Showing page {page} of {totalPages} ({total} total resources)
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							{resources.map((r) => (
								<ResourceCard
									key={r.id}
									resource={r}
									selectable
									selected={selectedResourceIds.includes(r.id)}
									onToggleSelect={handleToggleSelect}
									onRequestSelect={openCommentModal}
								/>
							))}
						</div>
						<div className="mt-6 flex items-center justify-between gap-3">
							<button
								type="button"
								onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
								disabled={page <= 1 || isFetching}
								className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Previous
							</button>

							<span className="text-sm text-gray-600">
								Page {page} of {totalPages}
							</span>

							<button
								type="button"
								onClick={() =>
									setPage((prev) => Math.min(prev + 1, totalPages))
								}
								disabled={page >= totalPages || isFetching}
								className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Next
							</button>
						</div>
					</>
				)}
			</main>
			<Footer />
			{commentModal.open && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Add Comment for Inquiry
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							{commentModal.resourceName}
						</p>
						<textarea
							value={commentModal.comment}
							onChange={(e) =>
								setCommentModal((prev) => ({
									...prev,
									comment: e.target.value,
								}))
							}
							rows={4}
							className="input-field resize-none"
							placeholder="Enter mandatory comment for this resource"
						/>
						<div className="mt-5 flex justify-end gap-2">
							<button
								type="button"
								onClick={closeCommentModal}
								className="btn-secondary"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmSelectionWithComment}
								disabled={!commentModal.comment.trim()}
								className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
